import { Suspense, lazy } from "react";
import { getLocaleUrl, pushEvent } from "@wbly/common";
import type { DataLayer } from "@wbly/common";
import { Button, Icon } from "@wbly/ui";
import type { CartSummary } from "@wbly/data-storefront";
import { iconsaxLock } from "@wbly/tokens/icon";
import { pushEcommerceEvent } from "@wbly/gtm";
import styles from "./PaymentOptions.module.css";

const ExpressCheckout = lazy(async () => {
  try {
    const module = await import("../../Cart/ExpressCheckout/ExpressCheckout");
    return { default: module.ExpressCheckout };
  } catch (error) {
    console.error("Failed to load ExpressCheckout component:", error);
    return { default: () => null };
  }
});

const PayPal = lazy(() =>
  import("./components/PayPal").then(({ PayPal }) => ({ default: PayPal }))
);

type PaymentOptionsProps = {
  staticContent: CartSummary;
  currency: string;
  locale: string;
  marketplace: string;
  payPalSdkUrl: string;
  stripeKey: string;
  iso: string;
  orderTotal: number;
};

export function PaymentOptions({
  staticContent,
  locale,
  currency,
  marketplace,
  payPalSdkUrl,
  stripeKey,
  iso,
  orderTotal,
}: PaymentOptionsProps) {
  const dataLayer = window.dataLayer as DataLayer[];
  const isFreeOrder = orderTotal === 0;

  return (
    <div className={styles.PaymentOptions}>
      {staticContent.hasExpressCheckout && !isFreeOrder && (
        <Suspense>
          <ExpressCheckout
            currency={currency}
            locale={locale}
            marketplace={marketplace}
            stripeKey={stripeKey}
            iso={iso}
          />
        </Suspense>
      )}

      {!staticContent.hidePayPalButton && !isFreeOrder && (
        <Suspense>
          <PayPal marketplace={marketplace} payPalSdkUrl={payPalSdkUrl} />
        </Suspense>
      )}

      <Button
        as="a"
        onClick={() => {
          pushEvent(
            dataLayer,
            "CartV3",
            "clicked_checkout_button",
            "standard_checkout"
          );
          pushEcommerceEvent({
            name: "begin_checkout",
            urgency: "low",
          });
        }}
        href={getLocaleUrl(marketplace, staticContent.checkoutButtonLink)}
      >
        <div className={styles["PaymentOptions-checkoutButton"]}>
          <Icon href={iconsaxLock} size="small" />
          <span>{staticContent.checkoutButtonLabel}</span>
        </div>
      </Button>
    </div>
  );
}
